import { DateTimePicker } from "@/components/ui/datetime";
import { Button } from "@/components/ui/button";
import { useEffect, useState, useRef } from "react";
import { MdClear } from "react-icons/md";

interface DateTimeProps {
    value: string | number | { seconds: number; nanoseconds?: number } | null;
    onChange?: (date: Date | null) => void;
}

export const DateTime = ({ value, onChange }: DateTimeProps) => {
    const [dateTime, setDateTime] = useState<Date | null>(null);
    const [isParsed, setIsParsed] = useState(false);
    const expectedTimestampRef = useRef<number | null>(null);

    const handleOnChange = (date: Date | null) => {
        if (!date) {
            console.log("Invalid date");
            setIsParsed(false);
            return;
        }
        setDateTime(date);
        setIsParsed(true);
        onChange?.(date);
    };

    const handleClearDate = () => {
        expectedTimestampRef.current = null;
        setDateTime(null);
        setIsParsed(false);
        onChange?.(null);
    };

    const handleSetToday = () => {
        // const today is today at noon
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const timestamp = Math.floor(today.getTime() / 1000);
        expectedTimestampRef.current = timestamp;
        console.log('[DateTime] Setting today:', today, 'timestamp:', timestamp);
        setDateTime(today);
        setIsParsed(true);
        onChange?.(today);
    };

    useEffect(() => {
        try {
            if (value === null || value === 0) {
                // Only reset if we're not expecting a value
                if (expectedTimestampRef.current === null) {
                    setIsParsed(false);
                    setDateTime(null);
                }
                return;
            }
            
            let parsedDate: Date;
            
            // Handle timestamp object: { seconds: number, nanoseconds?: number }
            if (value && typeof value === 'object' && 'seconds' in value && typeof value.seconds === 'number') {
                parsedDate = new Date(value.seconds * 1000);
                console.log('[DateTime] Parsed timestamp object:', { seconds: value.seconds, date: parsedDate.toISOString() });
            } else if (typeof value === "string") {
                // Check if it's an ISO date string (contains 'T' or matches ISO format)
                if (value.includes('T') || value.match(/^\d{4}-\d{2}-\d{2}/)) {
                    // Parse as ISO date string
                    parsedDate = new Date(value);
                } else {
                    // Assume it's a numeric timestamp string in seconds
                    const timestampSeconds = parseInt(value, 10);
                    if (isNaN(timestampSeconds)) {
                        throw new Error("Invalid timestamp string");
                    }
                    parsedDate = new Date(timestampSeconds * 1000);
                }
            } else if (typeof value === "number") {
                // Check if the number looks like it might be in milliseconds instead of seconds
                // If it's greater than 1e12, it's likely in milliseconds
                // If it's less than 1e10, it's likely in seconds
                let timestampMs: number;
                if (value > 1e12) {
                    // Value is in milliseconds, use directly
                    timestampMs = value;
                    console.warn('[DateTime] Value appears to be in milliseconds:', value, 'Treating as milliseconds');
                } else {
                    // Value is in seconds, convert to milliseconds
                    timestampMs = value * 1000;
                }
                parsedDate = new Date(timestampMs);
                console.log('[DateTime] Parsed number timestamp:', { 
                    value, 
                    treatedAs: value > 1e12 ? 'milliseconds' : 'seconds',
                    timestampMs,
                    date: parsedDate.toISOString(),
                    dateString: parsedDate.toLocaleDateString()
                });
            } else {
                throw new Error(`Invalid value type: ${typeof value}, value: ${JSON.stringify(value)}`);
            }
            
            console.log('[DateTime] useEffect - value:', value, 'parsedDate:', parsedDate.toISOString());
            
            if (!isNaN(parsedDate.getTime())) {
                const propTimestamp = Math.floor(parsedDate.getTime() / 1000);

                // If we're setting locally and the prop now matches, we're in sync
                if (expectedTimestampRef.current !== null && expectedTimestampRef.current === propTimestamp) {
                    expectedTimestampRef.current = null;
                    // State is already correct, no need to update
                    return;
                }

                // If we're expecting a different timestamp, wait for it
                if (expectedTimestampRef.current !== null) {
                    return;
                }

                // Normal update from prop
                setIsParsed(true);
                setDateTime(parsedDate);
            } else {
                throw new Error("Invalid Date");
            }
        } catch (error) {
            console.error('[DateTime] Error parsing date value:', value, error);
            if (expectedTimestampRef.current === null) {
                setIsParsed(false);
                setDateTime(null);
            }
        }
    }, [value]);

    return (
        <div className="flex items-center" style={{ zIndex: 99999 }}>
            {isParsed ? (
                <div className="flex flex-row items-center gap-2">
                    <div className="w-auto">
                        <DateTimePicker
                            key={`date-${dateTime?.getTime()}`}
                            value={dateTime ?? undefined}
                            onChange={(date) => handleOnChange(date || null)}
                            granularity="day"
                            className="w-auto"
                        />
                    </div>
                    <Button
                        onClick={handleClearDate}
                        variant="outline"
                        size="sm"
                        type="button"
                    >
                        <MdClear />
                    </Button>
                </div>
            ) : (
                <div className="flex flex-row items-center gap-2">
                    <div className="w-auto">
                        <DateTimePicker
                            key="date-empty"
                            value={undefined}
                            granularity="day"
                            onChange={(date) => handleOnChange(date || null)}
                            className="w-auto"
                        />
                    </div>
                    <Button
                        onClick={handleSetToday}
                        variant="outline"
                        size="sm"
                        type="button"
                    >
                        Now
                    </Button>
                </div>
            )}
        </div>
    );
};